import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../translations';
import SettingsMenu from './SettingsMenu';

const HeaderContainer = styled.header`
  background-color: ${props => props.theme === 'dark' ? '#121212' : '#ffffff'};
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
  padding: ${props => props.isScrolled ? '15px 0' : '20px 0'};
  transition: all 0.3s ease;
  border-bottom: 1px solid ${props => props.isScrolled ? (props.theme === 'dark' ? '#343a40' : '#e9ecef') : 'transparent'};
  box-shadow: ${props => props.isScrolled ? '0 5px 20px rgba(0, 0, 0, 0.1)' : 'none'};
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(motion.div)`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  display: flex;
  align-items: center;
`;

const Highlight = styled.span`
  color: #4a6cf7;
  font-weight: 700;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    position: fixed;
    top: ${props => props.isScrolled ? '65px' : '80px'};
    left: 0;
    width: 100%;
    background-color: ${props => props.theme === 'dark' ? '#121212' : '#ffffff'};
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
    padding: 20px;
    transform: translateY(${props => props.isOpen ? '0' : '-100%'});
    opacity: ${props => props.isOpen ? '1' : '0'};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transition: all 0.3s ease;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const NavList = styled(motion.ul)`
  display: flex;
  list-style: none;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
`;

const NavItem = styled(motion.li)`
  margin-left: 30px;
  
  @media (max-width: 768px) {
    margin: 10px 0;
    width: 100%;
  }
  
  &.desktop-only {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  &.mobile-only {
    display: none;
    
    @media (max-width: 768px) {
      display: block;
    }
  }
`;

const NavLink = styled.a`
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    color: #4a6cf7;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: #4a6cf7;
    transition: all 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
  
  @media (max-width: 768px) {
    display: block;
    padding: 10px 0;
  }
`;

const SignUpButton = styled(NavLink)`
  background-color: #4a6cf7;
  color: white !important;
  padding: 10px 20px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #3a56d4;
    transform: translateY(-2px);
  }
  
  &::after {
    display: none;
  }
`;

const ThemeToggle = styled.button`
  background: transparent;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  transition: all 0.3s ease;
  
  &:hover {
    transform: rotate(15deg);
  }
`;

const MobileMenuToggle = styled(motion.button)`
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 10px;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Hamburger = styled.span`
  display: block;
  position: relative;
  width: 24px;
  height: 2px;
  background-color: ${props => props.isOpen ? 'transparent' : (props.theme === 'dark' ? '#f8f9fa' : '#333')};
  transition: all 0.3s ease;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 24px;
    height: 2px;
    background-color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
    transition: all 0.3s ease;
  }
  
  &::before {
    top: ${props => props.isOpen ? '0' : '-8px'};
    transform: ${props => props.isOpen ? 'rotate(45deg)' : 'none'};
  }
  
  &::after {
    bottom: ${props => props.isOpen ? '0' : '-8px'};
    transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'none'};
  }
`;

const Header = ({ theme, toggleTheme }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const t = translations.header[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
  };
  
  const handleLanguageChange = (langCode) => {
    if (language !== langCode) {
      toggleLanguage();
    }
  };

  const navItems = [
    { name: t.solutions, href: '#solutions' },
    { name: t.features, href: '#features' },
    { name: t.contact, href: '#contact' }
  ];

  const languageOptions = [
    { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/gb.png' },
    { code: 'de', name: 'Deutsch', flag: 'https://flagcdn.com/w40/de.png' }
  ];

  return (
    <HeaderContainer isScrolled={isScrolled} theme={theme}>
      <HeaderInner>
        <Logo
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          theme={theme}
        >
          Explainable<Highlight>AI</Highlight> Solutions
        </Logo>
        
        <MobileMenuToggle
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          whileTap={{ scale: 0.9 }}
        >
          <Hamburger isOpen={mobileMenuOpen} theme={theme} />
        </MobileMenuToggle>
        
        <Nav isOpen={mobileMenuOpen} isScrolled={isScrolled} theme={theme}>
          <NavList
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            {navItems.map((item, index) => (
              <NavItem key={index} variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.6 }
                }
              }}>
                <NavLink 
                  href={item.href}
                  onClick={handleNavLinkClick}
                  theme={theme}
                >
                  {item.name}
                </NavLink>
              </NavItem>
            ))}
            <NavItem 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.6 }
                }
              }}
              className="desktop-only"
            >
              <SettingsMenu theme={theme} toggleTheme={toggleTheme} />
            </NavItem>
            <NavItem 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { duration: 0.6 }
                }
              }}
              className="mobile-only"
            >
              <div style={{ marginTop: '20px', marginBottom: '10px' }}>
                <h3 style={{ 
                  color: theme === 'dark' ? '#f8f9fa' : '#333', 
                  fontSize: '1rem', 
                  marginBottom: '15px' 
                }}>
                  Settings
                </h3>
                <div style={{ marginBottom: '15px' }}>
                  <h4 style={{ 
                    color: theme === 'dark' ? '#ced4da' : '#6c757d', 
                    fontSize: '0.9rem', 
                    fontWeight: '500',
                    marginBottom: '10px' 
                  }}>
                    Theme
                  </h4>
                  <div style={{ 
                    display: 'flex', 
                    background: theme === 'dark' ? '#121212' : '#f8f9fa',
                    borderRadius: '30px',
                    padding: '5px',
                    position: 'relative'
                  }}>
                    <button 
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: '8px 15px',
                        borderRadius: '30px',
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: 2,
                        color: theme === 'light' 
                          ? (theme === 'dark' ? '#121212' : '#ffffff')
                          : (theme === 'dark' ? '#f8f9fa' : '#333'),
                        fontWeight: theme === 'light' ? '600' : '400',
                        flex: 1,
                        textAlign: 'center'
                      }}
                      onClick={theme === 'dark' ? toggleTheme : undefined}
                    >
                      Light
                    </button>
                    <button 
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: '8px 15px',
                        borderRadius: '30px',
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: 2,
                        color: theme === 'dark' 
                          ? (theme === 'dark' ? '#121212' : '#ffffff')
                          : (theme === 'dark' ? '#f8f9fa' : '#333'),
                        fontWeight: theme === 'dark' ? '600' : '400',
                        flex: 1,
                        textAlign: 'center'
                      }}
                      onClick={theme === 'light' ? toggleTheme : undefined}
                    >
                      Dark
                    </button>
                    <div 
                      style={{
                        position: 'absolute',
                        width: '50%',
                        height: '80%',
                        borderRadius: '30px',
                        background: theme === 'dark' ? '#f8f9fa' : '#4a6cf7',
                        top: '10%',
                        left: theme === 'light' ? '5px' : '50%',
                        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                      }}
                    />
                  </div>
                </div>
                <div>
                  <h4 style={{ 
                    color: theme === 'dark' ? '#ced4da' : '#6c757d', 
                    fontSize: '0.9rem', 
                    fontWeight: '500',
                    marginBottom: '10px' 
                  }}>
                    Language
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {languageOptions.map((lang) => (
                      <button 
                        key={lang.code}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: language === lang.code ? 
                            (theme === 'dark' ? 'rgba(74, 108, 247, 0.2)' : 'rgba(74, 108, 247, 0.1)') : 
                            'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => handleLanguageChange(lang.code)}
                      >
                        <div 
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            marginRight: '10px',
                            backgroundImage: `url(${lang.flag})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                        />
                        <span 
                          style={{
                            color: language === lang.code ? '#4a6cf7' : (theme === 'dark' ? '#f8f9fa' : '#333'),
                            fontWeight: language === lang.code ? '600' : '400',
                            fontSize: '0.9rem',
                            flex: 1,
                            textAlign: 'left'
                          }}
                        >
                          {lang.name}
                        </span>
                        {language === lang.code && (
                          <span style={{ color: '#4a6cf7', marginLeft: '10px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </NavItem>
          </NavList>
        </Nav>
      </HeaderInner>
    </HeaderContainer>
  );
};

export default Header;
